// src/views/Audit/AuditTrail.jsx
import { useEffect, useMemo, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import TableHeader from "../../components/Tables/TableHeader";
import { Drawer, Select, Space, Tag, Tooltip, Divider } from "antd";
import { getAuditTrails } from "../../services/auditTrailService";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { BiShow } from "react-icons/bi";
import { AiOutlineFieldTime } from "react-icons/ai";
import { UserOutlined } from "@ant-design/icons";

const { Option } = Select;

const friendlyModel = (subjectType, logName) => {
  if (logName) {
    return logName || "—";
  } else {
    const parts = String(subjectType).split("\\");
    return parts[parts.length - 1] || subjectType;
  }

  // if (subjectType) {
  //   const parts = String(subjectType).split("\\");
  //   return parts[parts.length - 1] || subjectType;
  // }
  // return logName || "—";
};

const badgeForEvent = (event) => {
  const e = (event || "").toLowerCase();
  const styleBase = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "capitalize",
  };
  const styles = {
    created: { color: "#166534", background: "#dcfce7" },
    updated: { color: "#1d4ed8", background: "#dbeafe" },
    deleted: { color: "#991b1b", background: "#fee2e2" },
  };
  return <span style={{ ...styleBase, ...(styles[e] || {}) }}>{e || "—"}</span>;
};

const formatWhen = (iso) => {
  try {
    return format(new Date(iso), "dd MMM yyyy, HH:mm");
  } catch {
    return iso || "—";
  }
};

const changeSummary = (log) => {
  const props = log?.properties || {};
  const newVals = props.attributes || {};
  const oldVals = props.old || {};
  const keys = Array.from(
    new Set([...Object.keys(newVals), ...Object.keys(oldVals)])
  );

  if (log?.event === "created") {
    const nonEmpty = Object.keys(newVals || {}).length;
    return `${nonEmpty} field(s) set`;
  }
  if (log?.event === "deleted") {
    const nonEmpty = Object.keys(oldVals || {}).length;
    return `${nonEmpty} field(s) removed`;
  }
  if (log?.event === "updated") {
    return `${keys.length} field(s) changed`;
  }
  return "—";
};

const DiffRow = ({ field, oldValue, newValue }) => {
  const hasOld = typeof oldValue !== "undefined";
  const hasNew = typeof newValue !== "undefined";

  return (
    <div className="grid grid-cols-3 gap-3 text-sm items-start border-b last:border-b-0 py-2">
      <div className="font-medium text-gray-700 break-words">{field}</div>
      <div className="text-gray-600 break-words">
        {hasOld ? String(oldValue) : <span className="text-gray-400">—</span>}
      </div>
      <div className="text-gray-900 font-semibold break-words">
        {hasNew ? String(newValue) : <span className="text-gray-400">—</span>}
      </div>
    </div>
  );
};

const AuditTrail = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [eventFilter, setEventFilter] = useState("All");
  const [modelFilter, setModelFilter] = useState("All");
  const [userFilter, setUserFilter] = useState("All");

  // Drawer
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await getAuditTrails();
        const rows = data?.data || [];
        setLogs(rows);
        setFilteredLogs(rows);
      } catch (e) {
        toast.error("Failed to load audit trails");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Build dynamic filter options from data
  const modelOptions = useMemo(() => {
    const set = new Set(
      logs.map((l) => friendlyModel(l?.subject_type, l?.log_name))
    );
    return ["All", ...Array.from(set)];
  }, [logs]);

  const userOptions = useMemo(() => {
    const tuples = new Map(); // id -> label
    logs.forEach((l) => {
      const id = l?.user?.id;
      if (!id) return;
      const label =
        l?.user?.username ||
        l?.user?.email ||
        `${id?.slice(0, 6)}…${id?.slice(-4)}`;
      tuples.set(id, label);
    });
    return [
      "All",
      ...Array.from(tuples.entries()).map(([id, label]) => ({ id, label })),
    ];
  }, [logs]);

  useEffect(() => {
    let f = [...logs];

    // Search across username, email, role, institution, model, description
    if (searchTerm.trim() !== "") {
      const q = searchTerm.toLowerCase();
      f = f.filter((l) => {
        const uname = l?.user?.username || "";
        const email = l?.user?.email || "";
        const role = l?.user?.role || "";
        const inst = l?.user?.institution?.name || "";
        const model = friendlyModel(l?.subject_type, l?.log_name);
        const desc = l?.description || "";
        return (
          uname.toLowerCase().includes(q) ||
          email.toLowerCase().includes(q) ||
          role.toLowerCase().includes(q) ||
          inst.toLowerCase().includes(q) ||
          model.toLowerCase().includes(q) ||
          desc.toLowerCase().includes(q)
        );
      });
    }

    if (eventFilter !== "All") {
      f = f.filter(
        (l) => (l?.event || "").toLowerCase() === eventFilter.toLowerCase()
      );
    }
    if (modelFilter !== "All") {
      f = f.filter(
        (l) => friendlyModel(l?.subject_type, l?.log_name) === modelFilter
      );
    }
    if (userFilter !== "All") {
      f = f.filter((l) => l?.user?.id === userFilter);
    }

    setFilteredLogs(f);
  }, [logs, searchTerm, eventFilter, modelFilter, userFilter]);

  const columns = [
    {
      title: "#",
      width: 60,
      render: (_t, _r, i) => i + 1,
    },
    {
      title: "When",
      dataIndex: "created_at",
      width: 190,
      render: (v) => (
        <span className="inline-flex items-center gap-1">
          <AiOutlineFieldTime /> {formatWhen(v)}
        </span>
      ),
    },
    {
      title: "User",
      dataIndex: "user",
      width: 260,
      render: (u) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-800 inline-flex items-center gap-2">
            <UserOutlined /> {u?.username || u?.email || "—"}
          </span>
          <span className="text-xs text-gray-500">
            {u?.email || "—"} • {u?.role || "—"}
          </span>
        </div>
      ),
    },
    {
      title: "Institution",
      width: 220,
      render: (_, r) => (
        <span className="font-mono text-xs">
          {r?.user_institution?.name || "—"}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "event",
      width: 120,
      render: (e) => badgeForEvent(e),
    },
    {
      title: "Model",
      width: 180,
      render: (_, r) => (
        <Tag color="blue">{friendlyModel(r?.subject_type, r?.log_name)}</Tag>
      ),
    },
    {
      title: "Subject",
      width: 220,
      render: (_, r) => (
        <Tooltip title={r?.subject_id || "—"}>
          <span className="font-mono text-xs">
            {r?.subject_id ? `${r.subject_id.slice(0, 10)}…` : "—"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Summary",
      render: (r) => <span className="text-gray-700">{changeSummary(r)}</span>,
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View details">
            <button
              className="p-2 border border-blue-200 rounded-md text-blue-600 hover:bg-blue-50 transition"
              onClick={() => {
                setSelectedLog(record);
                setOpenDrawer(true);
              }}
            >
              <BiShow size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rows = filteredLogs.map((l) => ({ ...l, key: l.id }));

  const changedFields = useMemo(() => {
    const props = selectedLog?.properties || {};
    const newVals = props.attributes || {};
    const oldVals = props.old || {};
    const keys = Array.from(
      new Set([...Object.keys(newVals), ...Object.keys(oldVals)])
    );
    return keys.map((k) => ({
      field: k,
      oldValue: oldVals?.[k],
      newValue: newVals?.[k],
    }));
  }, [selectedLog]);

  // Filter bars
  const Filters = (
    <div className="mb-2 py-2 flex flex-wrap gap-2">
      <Select
        value={eventFilter}
        className="w-full md:w-[220px] h-[36px]"
        onChange={setEventFilter}
      >
        <Option value="All">All Events</Option>
        <Option value="created">Created</Option>
        <Option value="updated">Updated</Option>
        <Option value="deleted">Deleted</Option>
      </Select>

      <Select
        value={modelFilter}
        className="w-full md:w-[260px] h-[36px]"
        onChange={setModelFilter}
        showSearch
        filterOption={(input, option) =>
          option?.children?.toLowerCase().includes(input.toLowerCase())
        }
      >
        {modelOptions.map((m) => (
          <Option key={m} value={m}>
            {m}
          </Option>
        ))}
      </Select>

      <Select
        value={userFilter}
        className="w-full md:w-[260px] h-[36px]"
        onChange={setUserFilter}
        showSearch
        filterOption={(input, option) =>
          option?.children?.toLowerCase().includes(input.toLowerCase())
        }
      >
        <Option value="All">All Users</Option>
        {userOptions
          .filter((u) => u !== "All")
          .map((u) => (
            <Option key={u.id} value={u.id}>
              {u.label}
            </Option>
          ))}
      </Select>
    </div>
  );

  return (
    <>
      <PageHeading title="Audit Trail" subtitle1="System Audit" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Audit Trail"
            count={rows.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchAdd={false}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={rows}
            extra={Filters}
          />

          <Drawer
            title={
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-lg font-semibold text-gray-800">
                    {friendlyModel(
                      selectedLog?.subject_type,
                      selectedLog?.log_name
                    )}{" "}
                    • {badgeForEvent(selectedLog?.event)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatWhen(selectedLog?.created_at)}
                  </span>
                </div>
              </div>
            }
            width={720}
            onClose={() => setOpenDrawer(false)}
            open={openDrawer}
            placement="right"
          >
            {selectedLog ? (
              <div className="text-sm text-gray-700 space-y-6">
                {/* Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-gray-500">User</div>
                    <div className="font-medium">
                      {selectedLog?.user?.username ||
                        selectedLog?.user?.email ||
                        "—"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {selectedLog?.user?.email} • {selectedLog?.user?.role}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Institution</div>
                    <div className="font-medium">
                      {selectedLog?.user_institution?.name || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Subject</div>
                    <div className="font-mono text-xs break-all">
                      {selectedLog?.subject_id || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Log Name</div>
                    <div className="font-medium">
                      {selectedLog?.log_name || "—"}
                    </div>
                  </div>
                </div>

                <Divider />

                {/* Changes */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Changes
                  </h4>
                  {changedFields.length > 0 ? (
                    <div className="border rounded-md divide-y">
                      <div className="grid grid-cols-3 gap-3 text-xs font-semibold bg-gray-50 px-3 py-2 rounded-t-md">
                        <div>Field</div>
                        <div>Old</div>
                        <div>New</div>
                      </div>
                      <div className="px-3">
                        {changedFields.map((row) => (
                          <DiffRow
                            key={row.field}
                            field={row.field}
                            oldValue={row.oldValue}
                            newValue={row.newValue}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-500 italic">
                      No field-level differences recorded.
                    </div>
                  )}
                </div>

                {/* Raw Properties (optional) */}
                {selectedLog?.properties && (
                  <>
                    <Divider />
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        Raw Properties
                      </h4>
                      <pre className="bg-gray-50 border rounded-md p-3 text-xs overflow-x-auto">
                        {JSON.stringify(selectedLog.properties, null, 2)}
                      </pre>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No log selected.</p>
            )}
          </Drawer>
        </div>
      </div>
    </>
  );
};

export default AuditTrail;
